import { useEffect, useRef, useState } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import { Box, IconButton, InputBase } from '@mui/material';

type ExpandableSearchProps = {
  placeholder?: string;
  onSearch?: (query: string) => void;
  size?: 'sm' | 'md';
};

const ExpandableSearch: React.FC<ExpandableSearchProps> = ({
  placeholder = 'Search...',
  onSearch,
  size = 'md',
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const isSm = size === 'sm';
  const sizePx = isSm ? 36 : 44;

  useEffect(() => {
    if (open) {
      inputRef.current?.focus();
    }
  }, [open]);

  const handleQueryChange = (value: string) => {
    setQuery(value);
    onSearch?.(value);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        bgcolor: 'background.paper',
        transition: (theme) =>
          theme.transitions.create('width', {
            duration: theme.transitions.duration.shortest,
          }),
        width: open ? `${placeholder.length + 10}ch` : sizePx + 2,
        '&:hover, &:focus-within': {
          borderColor: 'primary.main',
        },
      }}
    >
      <IconButton
        aria-label="Search"
        onClick={() => setOpen(true)}
        {...(open && { disableRipple: true, tabIndex: -1 })}
        sx={{
          width: sizePx,
          height: sizePx,
          borderRadius: 2,
          flexShrink: 0,
          color: 'text.secondary',
          ...(open && {
            cursor: 'default',
            '&:hover': { bgcolor: 'transparent' },
          }),
        }}
      >
        <SearchIcon fontSize={isSm ? 'small' : 'medium'} />
      </IconButton>
      {open ? (
        <InputBase
          inputRef={inputRef}
          value={query}
          onChange={(event) => handleQueryChange(event.target.value)}
          onBlur={() => {
            if (!query.trim()) {
              setOpen(false);
            }
          }}
          placeholder={placeholder}
          sx={{
            flex: 1,
            pr: 1.5,
            fontSize: (theme) =>
              isSm
                ? theme.typography.caption.fontSize
                : theme.typography.body2.fontSize,
          }}
        />
      ) : null}
    </Box>
  );
};

export default ExpandableSearch;
